import type { FabricEditor } from 'modules/fabric-editor';
import { useEditorDirty, useSaveTemplate, useSaveHotkey } from 'modules/manager/ui/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { history } from 'store/store';
import Modal from 'ud-ui/components/modal';

interface UnsavedChangesGuardProps {
  editor: FabricEditor | null;
}

interface PendingTx {
  retry: () => void;
}

const UnsavedChangesGuard: React.FC<UnsavedChangesGuardProps> = ({ editor }) => {
  const isDirty = useEditorDirty(editor);
  const { save, isSaving } = useSaveTemplate(editor);
  const [pendingTx, setPendingTx] = useState<PendingTx | null>(null);

  useSaveHotkey(editor, save);

  useEffect(() => {
    if (!isDirty) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required for legacy browsers; modern browsers ignore custom strings.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;

    const unblock = history.block((tx) => {
      setPendingTx({
        retry: () => {
          unblock();
          tx.retry();
        },
      });
    });
    return unblock;
  }, [isDirty]);

  const handleCancel = useCallback(() => setPendingTx(null), []);

  const handleDiscard = useCallback(() => {
    const tx = pendingTx;
    if (!tx) return;
    setPendingTx(null);
    tx.retry();
  }, [pendingTx]);

  const handleSaveAndLeave = useCallback(async () => {
    const tx = pendingTx;
    if (!tx) return;
    await save();
    setPendingTx(null);
    tx.retry();
  }, [pendingTx, save]);

  return (
    <Modal show={pendingTx !== null} onClose={handleCancel} disableOverlayClick>
      <Modal.Header title="Несохранённые изменения" subtitle="У вас есть несохранённые правки в этом шаблоне. Что хотите сделать?" />
      <Modal.Footer justify="between">
        <button type="button" className="px-4 py-2 rounded-md text-gray-700 hover:bg-gray-100" onClick={handleCancel} disabled={isSaving}>
          Отмена
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            className="px-4 py-2 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50"
            onClick={handleDiscard}
            disabled={isSaving}
          >
            Не сохранять
          </button>
          <button
            type="button"
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            onClick={handleSaveAndLeave}
            disabled={isSaving}
          >
            {isSaving ? 'Сохранение…' : 'Сохранить и выйти'}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default UnsavedChangesGuard;
